import { Component, type JSX, Show } from 'solid-js';
import { COLOR_SWATCH_WRAPPER_SIZE_PX } from '../heatmap-stop-layout';
import {
  COLOR_SWATCH_BORDER_RADIUS_CSS,
  COLOR_SWATCH_PADDING_CSS,
  COLOR_SWATCH_SIZE_CSS,
  COLOR_SWATCH_WRAPPER_BORDER_RADIUS_CSS,
} from '../color-swatch-tokens';
import {
  isLightSwatchColor,
  SWATCH_BORDER_CLASS,
} from '../color-swatch-border';
import { paletteStopFocusBoxShadow } from '../palette-stop-focus-styles';
import { transparencyCheckerboardStyle } from '../gradient-preview/heatmap-css';

interface ColorSwatchProps {
  color: string;
  onClick?(e: MouseEvent): void;
  testId: string;
  /** Paints the Figma focus ring when the stop is selected or the picker is open. */
  selected?: boolean;
}

export const ColorSwatch: Component<ColorSwatchProps> = (props) => {
  const borderClass = (): string =>
    isLightSwatchColor(props.color) ? SWATCH_BORDER_CLASS : '';

  const swatchContainerStyle = (): JSX.CSSProperties => ({
    width: COLOR_SWATCH_SIZE_CSS,
    height: COLOR_SWATCH_SIZE_CSS,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'border-radius': COLOR_SWATCH_BORDER_RADIUS_CSS,
    ...(props.selected
      ? {
          // Ring on the swatch itself so it hugs the colour square, not the
          // padded wrapper.
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'box-shadow': paletteStopFocusBoxShadow,
        }
      : {}),
  });

  const swatchContainerClass = (): string =>
    `relative shrink-0 box-border overflow-hidden ${borderClass()} ${
      props.onClick ? 'cursor-pointer' : 'cursor-default'
    }`;

  const SwatchLayers = (): JSX.Element => (
    <>
      <div
        class="absolute inset-0"
        style={transparencyCheckerboardStyle()}
        data-testid={`${props.testId}-checkerboard`}
        aria-hidden="true"
      />
      <div
        class="absolute inset-0"
        style={{
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'background-color': props.color,
        }}
        data-testid={`${props.testId}-color`}
        aria-hidden="true"
      />
    </>
  );

  return (
    <div
      class="shrink-0 box-border"
      style={{
        width: `${String(COLOR_SWATCH_WRAPPER_SIZE_PX)}px`,
        height: `${String(COLOR_SWATCH_WRAPPER_SIZE_PX)}px`,
        padding: COLOR_SWATCH_PADDING_CSS,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'border-radius': COLOR_SWATCH_WRAPPER_BORDER_RADIUS_CSS,
      }}
    >
      <Show
        when={props.onClick}
        fallback={
          // Read-only swatch: a plain box so it does not look clickable.
          <div
            class={swatchContainerClass()}
            style={swatchContainerStyle()}
            data-testid={props.testId}
          >
            <SwatchLayers />
          </div>
        }
      >
        <button
          class={swatchContainerClass()}
          style={swatchContainerStyle()}
          onClick={(e) => props.onClick?.(e)}
          type="button"
          data-testid={props.testId}
        >
          <SwatchLayers />
        </button>
      </Show>
    </div>
  );
};
